import React, { useCallback, useEffect } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { Button, Form, Input, Row, Select, Space, Tooltip } from 'antd'
import produce from 'immer'
import { COUNTRY } from '@qflow/constants'

import { isoDateFromFormDate, isoDateToFormDate } from 'admin/lib/dates'
import { FormItem } from 'admin/lib/Forms'
import { Matchers } from 'admin/lib/Records'
import { useGlobalShortcut } from 'admin/lib/shortcuts'
import {
  CheckCompanyAddress,
  CheckCompanyName,
  CheckCompanyNumber,
  CheckCompanyResourceURI
} from 'admin/lib/SimpleDataValidation'
import * as Rules from 'admin/lib/ValidationRules'

type CompanyEditFormProps = {
  type: 'supplier' | 'manufacturer' | undefined
  company?: any
  onSave: (company: any) => void
  onCancel: () => void
}

export function CompanyEditForm({
  company = null,
  onSave,
  onCancel,
  type = undefined
}: CompanyEditFormProps) {
  // Form Setup
  const form = useForm({
    mode: 'all'
  })

  const { reset } = form
  useEffect(() => {
    if (company) {
      reset(transformCompanyToFormData(company))
    }
  }, [company, reset])

  // Callbacks
  const onSubmit = useCallback(
    async values => {
      const currentCompany = { ...company }
      const updatedCompany = transformFormDataToCompany(currentCompany, values)
      if (type) {
        onSave(updatedCompany)
      }
    },
    [company, onSave, type]
  )
  const handleSubmit = form.handleSubmit(onSubmit)

  useGlobalShortcut(Matchers.form.save, () => handleSubmit())

  const countryOptions = [
    { value: COUNTRY.AUSTRALIA, label: COUNTRY.AUSTRALIA },
    { value: COUNTRY.UK, label: COUNTRY.UK },
    { value: COUNTRY.USA, label: COUNTRY.USA }
  ]

  return (
    <FormProvider {...form}>
      <Form
        onFinish={handleSubmit}
        layout="vertical"
        style={{ marginTop: '1rem' }}
      >
        <FormItem
          name="name"
          label="Company Name"
          id="companyName"
          simpleDataValidationRules={CheckCompanyName}
          rules={Rules.MandatoryString}
        />

        <FormItem
          name="number"
          label="Company Number"
          id="number"
          simpleDataValidationRules={CheckCompanyNumber}
        />

        <FormItem
          name="address"
          label="Company Address"
          id="companyAddress"
          simpleDataValidationRules={CheckCompanyAddress}
        >
          <Input.TextArea />
        </FormItem>

        <FormItem
          name="url"
          label="Resource URI"
          id="url"
          simpleDataValidationRules={CheckCompanyResourceURI}
        />

        <FormItem
          name="country"
          label="Country"
          id="country"
          defaultValue={countryOptions[1].value}
          style={{ width: '100%' }}
        >
          <Select options={countryOptions} style={{ width: '100%' }} />
        </FormItem>

        {type === 'manufacturer' && (
          <FormItem name="keyTerms" label="Key Terms" id="keyTerms" />
        )}
        <Row justify="end" style={{ marginTop: '1rem' }}>
          <Space>
            <Tooltip
              title={!type ? 'You muct select Supplier or Manufacturer' : ''}
              placement="top"
            >
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: '8rem' }}
                disabled={!type}
              >
                Save
              </Button>
            </Tooltip>

            <Button onClick={onCancel} style={{ width: '8rem' }}>
              Cancel
            </Button>
          </Space>
        </Row>
      </Form>
    </FormProvider>
  )
}

export function transformCompanyToFormData(company) {
  return produce(company, draft => {
    draft.companyCertificates = draft.companyCertificates ?? []
    for (const cert of draft.companyCertificates) {
      cert.validFromDate = isoDateToFormDate(cert.validFromDate)
      cert.expirationDate = isoDateToFormDate(cert.expirationDate)
    }
    if (draft.keyTerms) {
      draft.keyTerms = draft.keyTerms.join(', ')
    }
  })
}

export function transformFormDataToCompany(currentCompany, values) {
  return produce(currentCompany, draft => {
    Object.assign(draft, values)

    draft.companyCertificates = (values.companyCertificates ?? []).map(
      formCert => {
        const currentCert = (currentCompany.companyCertificates ?? []).find(
          currentCert => formCert.certificateId === currentCert.certificateId
        )

        return produce(currentCert ?? {}, draftCert => {
          Object.assign(draftCert, formCert)

          draftCert.validFromDate = isoDateFromFormDate(formCert.validFromDate)
          draftCert.expirationDate = isoDateFromFormDate(
            formCert.expirationDate
          )
        })
      }
    )

    if (draft.keyTerms) {
      // convert comma separated string to array of strings and remove duplicates
      draft.keyTerms = [
        ...new Set(draft.keyTerms.split(',').map(item => item.trim()))
      ]
    }
  })
}
